/**
 * Types service — write record types (create / update). The catalog
 * (`./catalog.ts`) holds and loads them.
 *
 * A type row carries its default schema as `schema_id` only; the catalog
 * resolves `type.schema` from the schemas it holds. Every write returns the
 * written row and hands it to the catalog.
 */

import type { ResultOf } from "@graphql-typed-document-node/core"
import { AS_ADMIN } from "../asAdmin.js"
import { insertType, updateType } from "../gql/types.js"
import type { Mutate } from "../index.js"
import type { CatalogService } from "./catalog.js"

/** A type as the service returns it: its TypeFields row. */
export type TypeRow = NonNullable<ResultOf<typeof updateType>["update_types_by_pk"]>

export interface CreateTypeInput {
  name: string
  label: string
  plural_label: string
  description?: string | null
  position?: number
  schema_id?: number | null
}

/** Only the keys present are written. */
export interface UpdateTypePatch {
  label?: string
  plural_label?: string
  description?: string | null
  position?: number
  schema_id?: number | null
}

export function createTypesService({
  mutate,
  catalog,
}: {
  mutate: Mutate
  catalog: Pick<CatalogService, "written">
}) {
  const written = async (row: TypeRow): Promise<TypeRow> => {
    await catalog.written({ type: row })
    return row
  }

  const create = async (input: CreateTypeInput): Promise<TypeRow> => {
    const data = await mutate(insertType, {
      object: {
        name: input.name,
        label: input.label,
        plural_label: input.plural_label,
        description: input.description ?? null,
        position: input.position ?? 0,
        schema_id: input.schema_id ?? null,
      },
    }, AS_ADMIN)
    const row = data.insert_types_one
    if (!row) throw new Error("Failed to create type")
    return written(row)
  }

  /** `name` is the exact primary key (case-sensitive). */
  const update = async (name: string, patch: UpdateTypePatch): Promise<TypeRow> => {
    const data = await mutate(updateType, { name, _set: patch }, AS_ADMIN)
    const row = data.update_types_by_pk
    if (!row) throw new Error(`Type '${name}' not found`)
    return written(row)
  }

  return { create, update }
}
